import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { throwIfNotOk } from "@/lib/api-error";

export function CompanyInvitationsPanel({ userId }: { userId: string }) {
  const { i18n } = useTranslation();
  const en = i18n.language.startsWith("en");
  const client = useQueryClient();
  const invitations = useQuery<Array<{ id: string; companyName: string }>>({
    queryKey: ["employee-invitations", userId],
    queryFn: async () => {
      const response = await fetch("/api/employee-invitations");
      if (!response.ok) throw new Error(en ? "Could not load invitations" : "Pozivnice nisu učitane");
      return response.json();
    },
  });
  const respond = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "accept" | "decline" }) => {
      const response = await fetch(`/api/employee-invitations/${encodeURIComponent(id)}/${action}`, { method: "POST" });
      await throwIfNotOk(response, en ? "The invitation could not be updated." : "Pozivnica nije ažurirana.");
    },
    onSuccess: async () => { await client.invalidateQueries(); },
    // Greška se prikazuje ispod dugmadi; prazan onError gasi globalni toast da ne bude dva puta.
    onError: () => {},
  });
  if (invitations.isError) return <p role="alert">{invitations.error.message}</p>;
  if (!invitations.data?.length) return null;
  return <section className="mt-8 space-y-4 rounded-2xl border bg-white p-6">
    <h2 className="text-lg font-semibold">{en ? "Company invitations" : "Pozivnice firmi"}</h2>
    <p className="text-sm text-muted-foreground">{en
      ? "Accept an invitation to use the company's therapy credit. The company can view usage and booking details for sessions it funds after you join. Your private sessions and clinical notes are not shared."
      : "Prihvatite pozivnicu za korištenje kredita firme. Firma može vidjeti potrošnju i podatke o terminima koje finansira nakon vašeg pridruživanja. Privatne seanse i kliničke bilješke se ne dijele."}</p>
    {invitations.data.map(invite => <div key={invite.id} className="flex flex-wrap items-center gap-3">
      <span className="flex-1 font-medium">{invite.companyName}</span>
      <Button disabled={respond.isPending} onClick={() => respond.mutate({ id: invite.id, action: "accept" })}>{en ? "Accept invitation" : "Prihvati pozivnicu"}</Button>
      <Button variant="outline" disabled={respond.isPending} onClick={() => respond.mutate({ id: invite.id, action: "decline" })}>{en ? "Decline" : "Odbij"}</Button>
    </div>)}
    {respond.isError && <p role="alert" className="text-sm text-destructive">{respond.error.message}</p>}
  </section>;
}
